import React, { useState } from 'react';
import { Leaf, BarChart3, PieChart as PieChartIcon, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import { BiodiversityAssessment } from '../../utils/biodiversityMetrics';
import { Tree } from '../../types/tree';
import { NatureCard } from '../shared/NatureCard';
import { MetricDisplay } from '../shared/MetricDisplay';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './BiodiversitySection.css';

interface BiodiversitySectionProps {
  assessment: BiodiversityAssessment;
  trees: Tree[];
}

export const BiodiversitySection: React.FC<BiodiversitySectionProps> = ({ assessment, trees }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { diversity, conservation, structural, functional } = assessment;

  // Radar chart data for overall assessment
  const canopyTypeCount = Object.keys(functional.canopyTypes).length;
  const radarData = [
    {
      category: 'Species Diversity',
      value: Math.min((diversity.shannonIndex / 4) * 100, 100),
      fullMark: 100,
    },
    {
      category: 'Conservation Value',
      value: conservation.nativeProportion * 100,
      fullMark: 100,
    },
    {
      category: 'Structural Complexity',
      value: Math.min(structural.gbhDiversity.coefficient * 200, 100),
      fullMark: 100,
    },
    {
      category: 'Canopy Diversity',
      value: Math.min(canopyTypeCount * 20, 100),
      fullMark: 100,
    },
  ];

  // Conservation status data
  const conservationData = [
    { name: 'Native', count: conservation.nativeCount, color: '#27ae60' },
    { name: 'Endemic', count: conservation.endemicCount, color: '#8e44ad' },
    { name: 'Threatened', count: conservation.threatenedCount, color: '#e67e22' },
    { name: 'Invasive', count: conservation.invasiveCount, color: '#c0392b' },
  ];

  // Size distribution data
  const sizeData = [
    { name: 'Small', count: structural.sizeClasses.small, color: '#95a5a6' },
    { name: 'Medium', count: structural.sizeClasses.medium, color: '#3498db' },
    { name: 'Large', count: structural.sizeClasses.large, color: '#27ae60' },
    { name: 'Extra Large', count: structural.sizeClasses.extraLarge, color: '#2d5016' },
  ];

  return (
    <section className="biodiversity-section">
      <div className="biodiversity-section__container">
        <div
          className="biodiversity-section__header"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <Leaf size={48} className="biodiversity-section__header-icon" />
          <div style={{ flex: 1 }}>
            <h2 className="biodiversity-section__title">Biodiversity Assessment</h2>
            <p className="biodiversity-section__subtitle">
              Ecological diversity indices and conservation metrics
            </p>
          </div>
          {isExpanded ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
        </div>

        {isExpanded && (
          <>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Diversity Indices */}
        <h3 className="biodiversity-section__section-title">Diversity Indices</h3>
        <div className="biodiversity-section__grid">
          <MetricDisplay
            value={diversity.shannonIndex.toFixed(2)}
            label="Shannon Index (H')"
            icon={<BarChart3 size={32} />}
            comparison={diversity.interpretation.shannon}
            color="forest"
            size="medium"
            delay={0.1}
          />

          <MetricDisplay
            value={diversity.simpsonIndex.toFixed(3)}
            label="Simpson's Index (D)"
            icon={<PieChartIcon size={32} />}
            comparison={diversity.interpretation.simpson}
            color="blue"
            size="medium"
            delay={0.2}
          />

          <MetricDisplay
            value={diversity.evenness.toFixed(3)}
            label="Evenness (J')"
            icon={<TrendingUp size={32} />}
            comparison={diversity.interpretation.evenness}
            color="earth"
            size="medium"
            delay={0.3}
          />

          <MetricDisplay
            value={diversity.speciesRichness}
            label="Species Richness"
            icon={<Leaf size={32} />}
            comparison={`${diversity.totalIndividuals} total individuals`}
            color="forest"
            size="medium"
            delay={0.4}
          />
        </div>

        {/* Conservation Status */}
        <NatureCard className="biodiversity-section__chart">
          <h3 className="biodiversity-section__section-title">Conservation Status Distribution</h3>
          <p className="biodiversity-section__description">
            Tree counts by conservation significance
          </p>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={conservationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="name" stroke="#6c757d" tick={{ fill: '#6c757d', fontSize: 12 }} />
              <YAxis stroke="#6c757d" tick={{ fill: '#6c757d', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {conservationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </NatureCard>

        {/* Size Structure */}
        <NatureCard className="biodiversity-section__chart">
          <h3 className="biodiversity-section__section-title">Size Structure Distribution</h3>
          <p className="biodiversity-section__description">
            Age and size diversity across the population
          </p>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sizeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="name" stroke="#6c757d" tick={{ fill: '#6c757d', fontSize: 12 }} />
              <YAxis stroke="#6c757d" tick={{ fill: '#6c757d', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {sizeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="biodiversity-section__stats-summary">
            <div className="biodiversity-section__stat">
              <span className="biodiversity-section__stat-label">Average Height:</span>
              <span className="biodiversity-section__stat-value">{structural.heightDiversity.mean.toFixed(1)}m</span>
            </div>
            <div className="biodiversity-section__stat">
              <span className="biodiversity-section__stat-label">Average GBH:</span>
              <span className="biodiversity-section__stat-value">{structural.gbhDiversity.mean.toFixed(1)}cm</span>
            </div>
            <div className="biodiversity-section__stat">
              <span className="biodiversity-section__stat-label">Structural Complexity:</span>
              <span className="biodiversity-section__stat-value">{structural.structuralComplexity}</span>
            </div>
          </div>
        </NatureCard>
        </>
        )}
      </div>
    </section>
  );
};

export default BiodiversitySection;
